
import { ConnectionService } from './connection.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Receipt, ReceiptSort } from '@contracts/interfaces/receipt.interface';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { ReceiptFactory } from '../factories/receipt.factory';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface ReceiptCounts {
  unsorted: number;
  sorted: number;
  deleted: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReceiptsService {

    constructor(private http: HttpClient, private connectionService: ConnectionService) {}

    getReceipts(sort: ReceiptSort, filters: { amountMismatchOnly?: boolean } = {}) {
        return this.http
          .post<any[]>(this.connectionService.address + '/receipts-service/get', { sort, ...filters })
          .pipe(
            map((result: any) => this.sortByDateAdded((result.receipts || []).map(r => new ReceiptFactory(r))))
          );
    }

    getReceiptsByTransaction(transaction: Transaction) {
      return this.http.post<any[]>(this.connectionService.address + '/receipts-service/get-by-transaction', { transaction })
        .pipe(
          map((result: any) => (result.receipts || []).map(r => new ReceiptFactory(r)))
        );
    }

    updateReceipt(receipt: Receipt) {
      return this.http.post(this.connectionService.address + '/receipt-service/update', {receipt});
    }

    getReceiptURL (receipt: Receipt) {
      if (!receipt.file) {
        return "";
      }

      if (receipt.file.storageKey && receipt.file.storageKey.length > 0) {
        return "https://server.g72.com.au/" + receipt.file.path + "/" + receipt.file.storageKey;
      } else {
        return "https://server.g72.com.au/" + receipt.file.path + "/" + receipt.file.filename;
      }
    }

    hasReceipt(receipt: Receipt, transaction: Transaction) {
      for (let receiptOnTransaction of transaction.receipts) {
        if (receiptOnTransaction.sha1 == receipt.sha1) {
          return true;
        }
      }
    }

    removeDuplicateReceipts(transaction: Transaction): Transaction {
      const uniqueReceipts = [];
      const seen = new Set();

      for (const receipt of transaction.receipts) {
        if (!seen.has(receipt.sha1)) {
          seen.add(receipt.sha1);
          uniqueReceipts.push(receipt);
        }
      }

      transaction.receipts = uniqueReceipts;
      return transaction;
    }

    deleteReceipt(receipt: Receipt) {
        return this.http.post(this.connectionService.address + '/receipts-service/delete', {
          receipt
        });
    }

    getReceiptCounts(): Observable<ReceiptCounts> {
      return this.http.get<ReceiptCounts>(this.connectionService.address + '/receipts-service/counts');
    }

    public uploadReceipt(receipt: File[], transactionHash: string) {
      const formData: FormData = new FormData();
      formData.append('receipt', receipt[0]);
      if (typeof transactionHash === 'string' && transactionHash.trim().length > 0) {
        formData.append('transaction', transactionHash.trim());
      }

      return this.http.post(this.connectionService.address + '/receipt-service/upload-receipt', formData, {
        reportProgress: true,
        observe: 'events'
      });
    }

    private sortByDateAdded(receipts: Receipt[]): Receipt[] {
      return [...receipts].sort((a, b) => {
        const aDate = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
        const bDate = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;

        if (bDate !== aDate) {
          return bDate - aDate; // Newest first
        }

        // Fallback: keep stable order by date if dateAdded matches or missing
        const aFallback = a.date ? new Date(a.date).getTime() : 0;
        const bFallback = b.date ? new Date(b.date).getTime() : 0;
        return bFallback - aFallback;
      });
    }
}
